import { HeaderBar } from "@/features/common/components/HeaderBar";
import { useCreateMfaSecret } from "@/features/settings/api/useCreateMfaSecret";
import { useRemoveMfaSecret } from "@/features/settings/api/useRemoveMfaSecret";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { Limit } from "@/lib/common/Limit";
import { useFocusOnLoad } from "@/lib/common/useFocus";
import { useTitle } from "@/lib/common/useTitle";
import { useAppForm } from "@/lib/form/useAppForm";
import { useAuthState } from "@/lib/state";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Form } from "@/lib/form/components/Form";
import z from "zod";
import { Alert, AlertDescription } from "@/ds/shadcn/components/ui/alert";
import { CheckCircle2Icon } from "lucide-react";
import { LoadingState } from "@/ds/custom/LoadingState";
import { useSetMfaSecret } from "@/features/settings/api/useSetMfaSecret";
import * as QRCode from "qrcode";
import { useStore } from "@tanstack/react-form";
import { useNavigate } from "react-router";
import { LimitHeader } from "@/lib/common/LimitHeader";
import { LimitHeaderSubtitle } from "@/lib/common/LimitHeaderSubtitle";
import { LimitHeaderTitle } from "@/lib/common/LimitHeaderTitle";

export const PageMfa = () => {
	useTitle("MFA");

	const { mfa } = useAuthState().state;

	const breadcrumb = useMemo(() => [
		{ label: "Settings" },
		{ label: "Multi-factor Authentication" }
	], []);

	return (
		<>
			<HeaderBar
				title="Multi-factor Authentication"
				description="Configure and manage your multi-factor authentication settings"
				breadcrumb={breadcrumb} />

			{
				mfa
					? <MfaConfigured />
					: <MfaNotConfigured />
			}
		</>
	);
};

const MfaConfigured = () => {
	const removeMfaSecret = useRemoveMfaSecret();
	const ref = useFocusOnLoad();
	const navigate = useNavigate();

	const validators = useMemo(() => z.object({
		password: z.string().min(8, "Your password must be at least 8 characters"),
		code: z.string().regex(/^\d{6}$/, "Code must be a 6 digit number")
	}), []);

	const form = useAppForm({
		defaultValues: {
			password: "",
			code: ""
		},
		validators: {
			onMount: validators,
			onChange: validators
		},
		onSubmit: async ({ value }) => {
			await removeMfaSecret.mutateAsync({
				password: value.password,
				mfaCode: value.code
			});
		}
	});

	useEffect(() => {
		if (!removeMfaSecret.isSuccess) {
			return;
		}

		navigate("/login/mfa/disabled");
	}, [removeMfaSecret.isSuccess]);

	const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		form.handleSubmit();
	}, [form]);

	return (
		<>
			<Limit>
				<div className="mt-3">
					<Alert>
						<CheckCircle2Icon />
						<AlertDescription>
							Multi-factor Authentication Enabled
						</AlertDescription>
					</Alert>
				</div>
			</Limit>

			<Limit border={true}>
				<div className="flex flex-col gap-3">
					<LimitHeader>
						<LimitHeaderTitle>Disable MFA</LimitHeaderTitle>
						<LimitHeaderSubtitle>
							If you&apos;d like to disable multi-factor authentication for your account,
							please provide your account password and one of your 6-digit account codes
							below. Please note that you will be logged out after.
						</LimitHeaderSubtitle>
					</LimitHeader>

					<Form onSubmit={onSubmit}>
						<form.AppField name="password">
							{
								field => <field.TextField label="Password" type="password" ref={ref} required />
							}
						</form.AppField>

						<form.AppField name="code">
							{
								field => <field.MfaCodeField disabled={removeMfaSecret.isPending} />
							}
						</form.AppField>

						<ErrorDisplay error={removeMfaSecret.error} />

						<form.AppForm>
							<form.SubmitButton variant="destructive" label="Disable MFA" />
						</form.AppForm>
					</Form>
				</div>
			</Limit>
		</>
	);
};

const MfaNotConfigured = () => {
	const createMfaSecret = useCreateMfaSecret();
	const setMfaSecret = useSetMfaSecret();
	const ref = useFocusOnLoad();
	const [qrCode, setQRCode] = useState<string | null>(null);
	const navigate = useNavigate();

	const validators = useMemo(() => z.object({
		code: z.string().regex(/^\d{6}$/, "Code must be a 6-digit number")
	}), []);

	const form = useAppForm({
		defaultValues: {
			code: ""
		},
		validators: {
			onMount: validators,
			onChange: validators
		}
	});

	const code = useStore(form.store, x => x.values.code);

	const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
	}, []);

	useEffect(() => {
		createMfaSecret.mutate(undefined);
	}, []);

	useEffect(() => {
		if (!createMfaSecret.data?.body.mfaUrl) {
			return;
		}

		QRCode.toDataURL(
			createMfaSecret.data.body.mfaUrl,
			{ width: 768, color: { dark: "#444" }}
		).then(setQRCode);
	}, [createMfaSecret.data?.body.mfaUrl]);

	useEffect(() => {
		if (!setMfaSecret.isSuccess) {
			return;
		}

		navigate("/login/mfa/enabled");
	}, [setMfaSecret.isSuccess]);

	useEffect(() => {
		if (!form.state.isValid || !createMfaSecret.data?.body.mfaToken) {
			return;
		}

		setMfaSecret.mutate({
			mfaToken: createMfaSecret.data.body.mfaToken,
			mfaCode: code
		});
	}, [code, createMfaSecret.data?.body.mfaToken]);

	if (createMfaSecret.isPending) {
		return <LoadingState />;
	}

	if (createMfaSecret.isError) {
		return (
			<Limit border={true}>
				<ErrorDisplay error={createMfaSecret.error} />
			</Limit>
		);
	}

	return (
		<Limit border={true}>
			<div className="flex flex-col gap-3">
				<LimitHeader>
					<LimitHeaderTitle>Enable MFA</LimitHeaderTitle>
					<LimitHeaderSubtitle>
						If you&apos;d like to enable multi-factor authentication for your account,
						please scan the QR code with your authenticator app and enter the 6-digit code.
						Please note that you will be logged out after.
					</LimitHeaderSubtitle>
				</LimitHeader>

				{
					<Form onSubmit={onSubmit}>
						<form.AppField name="code">
							{
								field => <field.MfaCodeField ref={ref} disabled={setMfaSecret.isPending} />
							}
						</form.AppField>
						<ErrorDisplay error={setMfaSecret.error} />
					</Form>
				}

				{
					setMfaSecret.isPending &&
					<LoadingState />
				}

				{
					qrCode &&
					<div className="flex justify-center p-3">
						<img className="w-64 rounded-lg" src={qrCode} />
					</div>
				}
			</div>
		</Limit>
	);
};
